// AI Studio page (dashboard): type an idea, get a ready-to-edit animated template.
// Admins also get "Batch": many templates at once (AIBatch.jsx).

import React, { useContext, useEffect, useRef, useState } from "react";
import AuthContext from "../context/userAuth.context";
import { useAIJobs, RUNNING, BATCH_TAB_KEY } from "./AIJobsContext";
import AISettings from "./AISettings";
import { AIBatchForm, AIBatchList } from "./AIBatch";
import AISizePicker, { lastSize, sizeName } from "./AISizePicker";
import { aiApi } from "../Utils/aiTemplate/aiApi";

const STYLES = [
  { key: "auto", label: "Auto", icon: "auto_awesome" },
  { key: "corporate", label: "Corporate", icon: "business_center" },
  { key: "playful", label: "Playful", icon: "mood" },
  { key: "bold", label: "Bold", icon: "whatshot" },
  { key: "elegant", label: "Elegant", icon: "spa" },
  { key: "minimal", label: "Minimal", icon: "crop_square" },
];

const EXAMPLES = [
  "Dental clinic grand opening",
  "Summer fashion sale",
  "Real estate agency",
  "Mobile app launch",
  "Cozy coffee shop",
  "Gym membership offer",
  "Online English course",
  "Wedding photography",
];

const PREFILL_KEY = "toons_ai_prefill";

const thumbUrl = (api, path) => (path ? String(api).replace(/\/$/, "") + "/" + String(path).replace(/^\//, "") : null);

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return mins + " min ago";
  const h = Math.round(mins / 60);
  if (h < 24) return h + " h ago";
  return d.toLocaleDateString();
}

function ModeBadge({ settings, onClick }) {
  if (!settings) return null;
  let text;
  if (settings.mode === "own" && settings.hasKey) text = `${settings.provider === "openai" ? "OpenAI" : "Claude"} key …${settings.keyHint}`;
  else if (settings.credits && settings.credits.unlimited) text = "Toons AI · unlimited";
  else if (settings.credits) text = `Toons AI · ${settings.credits.remaining} credit${settings.credits.remaining === 1 ? "" : "s"} left`;
  else text = "Toons AI";
  return (
    <button className="ais-mode" onClick={onClick} title="AI settings">
      <span className="material-icons">{settings.mode === "own" ? "vpn_key" : "flash_on"}</span>
      {text}
      <span className="material-icons ais-mode-gear">settings</span>
    </button>
  );
}

/** Error message under the input, with a shortcut when there is a fix. */
function Problem({ res, onSettings }) {
  if (!res || res.status) return null;
  const toSettings = ["no_credits", "toons_unavailable", "no_key"].includes(res.code);
  return (
    <div className="ais-problem">
      <span className="material-icons">info</span>
      <span>{res.message}</span>
      {toSettings && <button className="ais-link light" onClick={onSettings}>Open AI settings</button>}
    </div>
  );
}

export default function AIStudio() {
  const { authenticationData, getLink } = useContext(AuthContext);
  const api = authenticationData.ApiBaseURL;
  const { available, settings, jobs, generate, openPanel, openProject, historyVersion, reloadSettings } = useAIJobs();

  const [keyword, setKeyword] = useState(() => {
    try {
      const v = sessionStorage.getItem(PREFILL_KEY) || "";
      sessionStorage.removeItem(PREFILL_KEY);
      return v;
    } catch (e) {
      return "";
    }
  });
  const [style, setStyle] = useState("auto");
  const [presenter, setPresenter] = useState(true);
  const [canvasSize, setCanvasSize] = useState(lastSize);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [history, setHistory] = useState(null);
  const [placeholder, setPlaceholder] = useState(0);
  const [tab, setTab] = useState(() => {
    try {
      const v = sessionStorage.getItem(BATCH_TAB_KEY);
      sessionStorage.removeItem(BATCH_TAB_KEY);
      return v === "batch" ? "batch" : "single";
    } catch (e) {
      return "single";
    }
  });
  const [batchKey, setBatchKey] = useState(0);
  const input = useRef(null);
  const isAdmin = !!(settings && settings.isAdmin);
  const batchMode = isAdmin && tab === "batch";

  useEffect(() => {
    reloadSettings();
    if (input.current) input.current.focus();
  }, [reloadSettings]);

  useEffect(() => {
    const t = setInterval(() => setPlaceholder((p) => (p + 1) % EXAMPLES.length), 2600);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let off = false;
    aiApi.history(api, 24).then((res) => !off && setHistory(res.status ? res.jobs || [] : []));
    return () => {
      off = true;
    };
  }, [api, historyVersion]);

  const run = async (text, size) => {
    const k = (text != null ? text : keyword).trim();
    if (k.length < 2) {
      setProblem({ status: false, message: "Type a few words about your video, for example “dental clinic opening”." });
      if (input.current) input.current.focus();
      return;
    }
    setBusy(true);
    setProblem(null);
    const res = await generate({ keyword: k, style, presenter, canvasSize: size || canvasSize });
    setBusy(false);
    if (!res.status) setProblem(res);
  };

  // Live state (this session) over the server's list.
  const live = new Map(jobs.map((j) => [j.id, j]));
  const list = (history || []).map((h) => ({ ...h, ...(live.get(h.id) || {}) }));
  jobs.forEach((j) => {
    if (!list.some((x) => x.id === j.id)) list.unshift(j);
  });

  const credits = settings && settings.credits;
  const outOfCredits = settings && settings.mode === "toons" && credits && !credits.unlimited && credits.remaining <= 0;

  return (
    <div className="ais-page">
      <div className="ais-hero">
        <div className="ais-hero-bg" />
        <div className="ais-hero-top">
          <span className="ais-pill"><span className="material-icons">auto_awesome</span> AI Studio</span>
          {isAdmin && (
            <div className="ais-tabs">
              <button className={"ais-tab" + (tab === "single" ? " is-on" : "")} onClick={() => setTab("single")}>
                <span className="material-icons">auto_awesome</span> One template
              </button>
              <button className={"ais-tab" + (tab === "batch" ? " is-on" : "")} onClick={() => setTab("batch")}>
                <span className="material-icons">dynamic_feed</span> Batch
              </button>
            </div>
          )}
          <ModeBadge settings={settings} onClick={() => setShowSettings(!showSettings)} />
        </div>
        {batchMode ? (
          <>
            <h2 className="ais-hero-h">Create many templates at once.</h2>
            <p className="ais-hero-p">
              One topic per line. Each becomes a finished template, saved in your templates and published in the categories you choose.
            </p>
            <AIBatchForm api={api} onStarted={() => setBatchKey((k) => k + 1)} />
          </>
        ) : (
        <>
        <h2 className="ais-hero-h">Describe your video. Get a ready-made animated template.</h2>
        <p className="ais-hero-p">
          Scenes, texts, colours, a talking presenter and animations, designed for you in under a minute. Edit anything afterwards.
        </p>

        <div className={"ais-prompt" + (busy ? " is-busy" : "")}>
          <span className="material-icons ais-prompt-icon">auto_awesome</span>
          <input
            ref={input}
            className="ais-prompt-input"
            value={keyword}
            maxLength={120}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && run()}
            placeholder={"e.g. " + EXAMPLES[placeholder]}
          />
          <button className="ais-generate" onClick={() => run()} disabled={busy || !available}>
            {busy ? <span className="ais-spinner light" /> : <span className="material-icons">auto_awesome</span>}
            Generate
          </button>
        </div>

        <AISizePicker value={canvasSize} onChange={setCanvasSize} />

        <div className="ais-controls">
          <div className="ais-chips">
            {STYLES.map((s) => (
              <button key={s.key} className={"ais-chip" + (style === s.key ? " is-on" : "")} onClick={() => setStyle(s.key)}>
                <span className="material-icons">{s.icon}</span>
                {s.label}
              </button>
            ))}
          </div>
          <label className="ais-switch">
            <input type="checkbox" checked={presenter} onChange={(e) => setPresenter(e.target.checked)} />
            <span className="ais-switch-track"><span /></span>
            Talking presenter
          </label>
        </div>

        <div className="ais-examples">
          <span>Try:</span>
          {EXAMPLES.slice(0, 6).map((ex) => (
            <button key={ex} className="ais-example" onClick={() => { setKeyword(ex); if (input.current) input.current.focus(); }}>
              {ex}
            </button>
          ))}
        </div>

        <Problem res={problem} onSettings={() => setShowSettings(true)} />
        </>
        )}
        {!available && (
          <div className="ais-problem">
            <span className="material-icons">cloud_off</span>
            <span>AI Studio needs the latest API update on the server.</span>
          </div>
        )}
        {outOfCredits && !problem && (
          <div className="ais-problem">
            <span className="material-icons">flash_on</span>
            <span>You&rsquo;ve used this month&rsquo;s AI credits.</span>
            {getLink && (
              <a className="ais-link light" href={getLink()} target="_blank" rel="noopener noreferrer">Upgrade</a>
            )}
            <button className="ais-link light" onClick={() => setShowSettings(true)}>Use my own key</button>
          </div>
        )}
      </div>

      {showSettings && <AISettings onClose={() => setShowSettings(false)} />}

      {batchMode ? (
        <>
          <div className="ais-section-head">
            <div className="ais-h3">Your batches</div>
            <div className="ais-muted">Templates appear here as they are finished.</div>
          </div>
          <AIBatchList api={api} refreshKey={batchKey} />
        </>
      ) : (
      <>
      <div className="ais-section-head">
        <div className="ais-h3">Your AI templates</div>
        <div className="ais-muted">Saved in My Projects as soon as they are ready.</div>
      </div>

      {history === null && <div className="ais-muted ais-pad">Loading...</div>}
      {history !== null && list.length === 0 && (
        <div className="ais-empty">
          <span className="material-icons">movie_filter</span>
          <div>Your generated templates will appear here.</div>
        </div>
      )}
      <div className="ais-grid">
        {list.map((j) => {
          const running = RUNNING.includes(j.status);
          const img = (j.previews && j.previews.find(Boolean)) || thumbUrl(api, j.previewImagePath);
          return (
            <div key={j.id} className={"ais-tile is-" + j.status}>
              <button
                className="ais-tile-media"
                onClick={() => (j.status === "done" && j.projectId ? openProject(j) : openPanel(j.id))}
                title={j.status === "done" ? "Open in editor" : "Show progress"}
              >
                {img && j.status === "done" ? (
                  <img src={img} alt="" />
                ) : running ? (
                  <div className="ais-tile-running">
                    <span className="material-icons ais-spin-slow">auto_awesome</span>
                    <div className="ais-mini-bar"><span style={{ width: Math.max(4, j.progress || 4) + "%" }} /></div>
                  </div>
                ) : (
                  <div className="ais-tile-off">
                    <span className="material-icons">{j.status === "cancelled" ? "block" : "error_outline"}</span>
                  </div>
                )}
                {j.status === "done" && <span className="ais-tile-open"><span className="material-icons">edit</span> Open</span>}
              </button>
              <div className="ais-tile-info">
                <div className="ais-tile-title" title={j.projectName || j.keyword}>{j.projectName || j.keyword}</div>
                <div className="ais-tile-meta">
                  <span className={"ais-status s-" + j.status}>
                    {running ? "Generating" : j.status === "done" ? "Ready" : j.status === "cancelled" ? "Cancelled" : "Failed"}
                  </span>
                  <span className="ais-muted">
                    {j.canvasSize && j.canvasSize !== "16:9" ? sizeName(j.canvasSize) + " · " : ""}
                    {when(j.createdAt)}
                  </span>
                </div>
                {(j.status === "failed" || j.status === "cancelled") && (
                  <button className="ais-link" onClick={() => { setKeyword(j.keyword); run(j.keyword, j.canvasSize); }}>Try again</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </>
      )}
    </div>
  );
}

/** Opens AI Studio with the keyword already typed (from the dashboard banner). */
export function prefillAIStudio(keyword) {
  try {
    sessionStorage.setItem(PREFILL_KEY, keyword || "");
  } catch (e) {
    /* ignore */
  }
}
